import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { adminApi, formatDate, formatApiError } from "../lib/api";
import { ArrowLeft, Loader2, ShieldAlert, Ban, CheckCircle2, Search, Users as UsersIcon, Upload, Tv, Copy, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin-only page.
 * Shows every host account with usage stats and lets an admin block/unblock
 * accounts (e.g. for non-payment). Non-admins are bounced back to /dashboard.
 */
export default function AdminPanel() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState(null);

  // Gate: only admins may see this page. Anyone else is redirected.
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/login");
      return;
    }
    if (user.role !== "admin") {
      navigate("/dashboard");
    }
  }, [user, authLoading, navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.listUsers();
      setUsers(data?.users || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role === "admin") load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) =>
        (u.email || "").toLowerCase().includes(q) ||
        (u.name || "").toLowerCase().includes(q)
    );
  }, [users, search]);

  const block = async (u) => {
    const reason = window.prompt(
      `Motivo do bloqueio da conta ${u.email}?`,
      "Falta de pagamento"
    );
    if (reason == null) return; // cancel
    setBusyId(u.id);
    try {
      await adminApi.blockUser(u.id, reason || "Falta de pagamento");
      toast.success(`${u.email} bloqueado`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const unblock = async (u) => {
    if (!window.confirm(`Desbloquear ${u.email}?`)) return;
    setBusyId(u.id);
    try {
      await adminApi.unblockUser(u.id);
      toast.success(`${u.email} desbloqueado`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const totals = useMemo(() => ({
    total: users.length,
    blocked: users.filter((u) => u.is_blocked).length,
    events: users.reduce((s, u) => s + (u.event_count || 0), 0),
    media: users.reduce((s, u) => s + (u.media_count || 0), 0),
  }), [users]);

  if (!user || user.role !== "admin") return null;

  return (
    <div className="min-h-screen bg-[#FFFBF7]" data-testid="admin-panel-page">
      <div className="max-w-6xl mx-auto p-4 sm:p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/dashboard")}
              className="w-10 h-10 flex items-center justify-center bg-white border-2 border-[#1C1917] rounded-full shadow-[2px_2px_0px_#1C1917] hover:translate-y-[1px] hover:shadow-[1px_1px_0px_#1C1917] transition-all"
              data-testid="admin-back-btn"
            >
              <ArrowLeft className="w-5 h-5" strokeWidth={2.5} />
            </button>
            <div className="flex items-center gap-2">
              <ShieldAlert className="w-6 h-6 text-[#FF6B6B]" strokeWidth={2.5} />
              <h1 className="text-2xl sm:text-3xl font-black text-[#1C1917]">
                Painel Admin
              </h1>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-6">
          <StatCard label="Contas" value={totals.total} />
          <StatCard label="Bloqueadas" value={totals.blocked} accent={totals.blocked > 0} />
          <StatCard label="Eventos" value={totals.events} />
          <StatCard label="Midias" value={totals.media} />
        </div>

        {/* TV APK hosting card */}
        <TvApkHostingCard />

        {/* Search */}
        <div className="flex items-center gap-2 bg-white border-2 border-[#1C1917] rounded-full px-4 py-2 mb-4 shadow-[3px_3px_0px_#1C1917]">
          <Search className="w-4 h-4 text-[#78716C]" strokeWidth={2.5} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por email ou nome..."
            className="flex-1 bg-transparent focus:outline-none text-sm"
            data-testid="admin-search-input"
          />
          {search && (
            <button onClick={() => setSearch("")} className="text-xs font-bold text-[#78716C] hover:text-[#FF6B6B]">
              Limpar
            </button>
          )}
        </div>

        {/* Users list */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-[#FF6B6B]" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 bg-white border-2 border-[#1C1917] rounded-xl shadow-[3px_3px_0px_#1C1917]">
            <UsersIcon className="w-10 h-10 mx-auto text-[#78716C] mb-2" />
            <p className="text-[#78716C] text-sm">Nenhuma conta encontrada.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map((u) => (
              <UserRow
                key={u.id}
                u={u}
                busy={busyId === u.id}
                onBlock={() => block(u)}
                onUnblock={() => unblock(u)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className={`bg-white border-2 border-[#1C1917] rounded-xl px-3 py-3 shadow-[3px_3px_0px_#1C1917] ${accent ? "ring-2 ring-[#FF6B6B]" : ""}`}>
      <p className="text-[10px] text-[#78716C] uppercase font-bold tracking-wider">{label}</p>
      <p className="text-2xl font-black text-[#1C1917]">{value}</p>
    </div>
  );
}

/**
 * TV APK hosting card.
 *
 * Lets the admin upload the freshly compiled Festa.IA TV APK so guests can
 * sideload it on Fire TV / Android TV by typing the short public URL on
 * the Downloader app. We deliberately keep state local (no global store):
 * this card is mounted only for admins and the info endpoint is cheap.
 */
function TvApkHostingCard() {
  const [info, setInfo] = useState({ available: false, size_bytes: 0 });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [copied, setCopied] = useState(false);

  const publicUrl = `${window.location.origin}/api/tv-apk`;

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.tvApkInfo();
      setInfo(data || { available: false, size_bytes: 0 });
    } catch (e) {
      // info is public so this should never fail; keep silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-uploading the same file later
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".apk")) {
      toast.error("Selecione um arquivo .apk");
      return;
    }
    if (file.size < 10_000) {
      toast.error("Arquivo .apk parece invalido (muito pequeno)");
      return;
    }
    if (file.size > 200 * 1024 * 1024) {
      toast.error("APK maior que 200MB nao e suportado");
      return;
    }
    setUploading(true);
    setProgress(0);
    try {
      await adminApi.uploadTvApk(file, (p) => {
        if (p.total) setProgress(Math.round((p.loaded / p.total) * 100));
      });
      toast.success("APK publicado com sucesso!");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      toast.success("URL copiada");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Nao foi possivel copiar");
    }
  };

  const sizeMb = info.size_bytes ? (info.size_bytes / (1024 * 1024)).toFixed(2) : "0";

  return (
    <div
      className="mb-6 bg-white border-2 border-[#1C1917] rounded-xl p-4 shadow-[3px_3px_0px_#1C1917]"
      data-testid="admin-tv-apk-card"
    >
      <div className="flex items-center gap-2 mb-2">
        <Tv className="w-5 h-5 text-[#1C1917]" strokeWidth={2.5} />
        <h2 className="text-lg font-black text-[#1C1917]">Hospedagem do APK da TV</h2>
        {!loading && (
          info.available ? (
            <span
              className="ml-auto bg-[#22C55E] text-white text-[9px] font-bold uppercase px-2 py-0.5 rounded-full"
              data-testid="admin-tv-apk-status-available"
            >
              Publicado · {sizeMb} MB
            </span>
          ) : (
            <span
              className="ml-auto bg-[#78716C] text-white text-[9px] font-bold uppercase px-2 py-0.5 rounded-full"
              data-testid="admin-tv-apk-status-empty"
            >
              Nao publicado
            </span>
          )
        )}
      </div>

      <p className="text-xs text-[#78716C] mb-3">
        Suba o .apk gerado no pwabuilder.com. Convidados na Smart TV poderao baixar
        digitando a URL curta abaixo no app <span className="font-bold">Downloader</span>.
      </p>

      {/* Public URL row */}
      <div className="flex items-center gap-2 bg-[#FFFBF7] border-2 border-[#1C1917] rounded-lg px-3 py-2 mb-3">
        <code
          className="flex-1 text-xs sm:text-sm font-mono text-[#1C1917] truncate"
          data-testid="admin-tv-apk-public-url"
        >
          {publicUrl}
        </code>
        <button
          onClick={copyUrl}
          className="shrink-0 flex items-center gap-1 bg-[#1C1917] text-white text-[10px] font-bold uppercase px-2 py-1 rounded-full hover:opacity-80 transition-opacity"
          data-testid="admin-tv-apk-copy-url"
        >
          {copied ? <Check className="w-3 h-3" strokeWidth={3} /> : <Copy className="w-3 h-3" strokeWidth={3} />}
          {copied ? "Copiada" : "Copiar"}
        </button>
      </div>

      {/* Upload control */}
      <label
        htmlFor="tv-apk-file-input"
        className={`flex items-center justify-center gap-2 w-full bg-[#FF6B6B] text-white border-2 border-[#1C1917] text-sm font-bold uppercase px-4 py-2.5 rounded-full shadow-[3px_3px_0px_#1C1917] hover:translate-y-[1px] hover:shadow-[1px_1px_0px_#1C1917] transition-all cursor-pointer ${
          uploading ? "opacity-60 pointer-events-none" : ""
        }`}
        data-testid="admin-tv-apk-upload-label"
      >
        {uploading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Enviando {progress}%
          </>
        ) : (
          <>
            <Upload className="w-4 h-4" strokeWidth={3} />
            {info.available ? "Publicar nova versao" : "Publicar APK"}
          </>
        )}
        <input
          id="tv-apk-file-input"
          type="file"
          accept=".apk,application/vnd.android.package-archive"
          onChange={handleFile}
          disabled={uploading}
          className="hidden"
          data-testid="admin-tv-apk-file-input"
        />
      </label>

      {uploading && (
        <div className="mt-2 h-1.5 w-full bg-[#FFFBF7] border-2 border-[#1C1917] rounded-full overflow-hidden">
          <div
            className="h-full bg-[#22C55E] transition-all"
            style={{ width: `${progress}%` }}
            data-testid="admin-tv-apk-progress"
          />
        </div>
      )}
    </div>
  );
}

function UserRow({ u, busy, onBlock, onUnblock }) {
  return (
    <div
      className={`flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white border-2 border-[#1C1917] rounded-xl p-3 shadow-[3px_3px_0px_#1C1917] ${
        u.is_blocked ? "bg-red-50" : ""
      }`}
      data-testid={`admin-user-row-${u.id}`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-black text-[#1C1917] truncate">{u.name || "(sem nome)"}</p>
          {u.role === "admin" && (
            <span className="bg-[#1C1917] text-white text-[9px] font-bold uppercase px-2 py-0.5 rounded-full">
              Admin
            </span>
          )}
          {u.is_blocked && (
            <span className="bg-red-600 text-white text-[9px] font-bold uppercase px-2 py-0.5 rounded-full flex items-center gap-1">
              <Ban className="w-3 h-3" strokeWidth={3} /> Bloqueada
            </span>
          )}
        </div>
        <p className="text-xs text-[#78716C] truncate">{u.email}</p>
        <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-[10px] text-[#78716C]">
          <span>{u.event_count} eventos</span>
          <span>{u.media_count} midias</span>
          <span>{u.paid_events} pagos</span>
          {u.created_at && <span>desde {formatDate(u.created_at.split("T")[0])}</span>}
          {u.last_payment?.status && (
            <span>
              ultimo pgto: <span className="font-bold">{u.last_payment.status}</span>
            </span>
          )}
        </div>
        {u.is_blocked && u.blocked_reason && (
          <p className="text-[10px] text-red-700 mt-1 font-bold">Motivo: {u.blocked_reason}</p>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {u.role !== "admin" && (
          u.is_blocked ? (
            <button
              onClick={onUnblock}
              disabled={busy}
              className="flex items-center gap-1 bg-[#22C55E] text-white border-2 border-[#1C1917] text-xs font-bold uppercase px-3 py-1.5 rounded-full shadow-[2px_2px_0px_#1C1917] hover:translate-y-[1px] hover:shadow-[1px_1px_0px_#1C1917] transition-all disabled:opacity-60"
              data-testid={`admin-unblock-btn-${u.id}`}
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" strokeWidth={3} />}
              Desbloquear
            </button>
          ) : (
            <button
              onClick={onBlock}
              disabled={busy}
              className="flex items-center gap-1 bg-red-600 text-white border-2 border-[#1C1917] text-xs font-bold uppercase px-3 py-1.5 rounded-full shadow-[2px_2px_0px_#1C1917] hover:translate-y-[1px] hover:shadow-[1px_1px_0px_#1C1917] transition-all disabled:opacity-60"
              data-testid={`admin-block-btn-${u.id}`}
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Ban className="w-3 h-3" strokeWidth={3} />}
              Bloquear
            </button>
          )
        )}
      </div>
    </div>
  );
}
